import { useState, useEffect, useCallback } from "react";
import { apiFetch, ApiError } from "@/lib/api-client";
import type {
  BulkImportTypeDefinition,
  BulkImportTypesResponse,
} from "../types";

interface UseBulkImportTypesResult {
  types: readonly BulkImportTypeDefinition[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useBulkImportTypes(): UseBulkImportTypesResult {
  const [types, setTypes] = useState<readonly BulkImportTypeDefinition[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [fetchKey, setFetchKey] = useState(0);

  useEffect(() => {
    const fetchTypes = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const data = await apiFetch.get<BulkImportTypesResponse>(
          "/api/bulk-imports/types",
        );
        setTypes(data.types);
      } catch (err) {
        setError(toError(err));
        setTypes([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTypes();
  }, [fetchKey]);

  const refetch = useCallback(() => setFetchKey((key) => key + 1), []);

  return {
    types,
    isLoading,
    error,
    refetch,
  };
}

function toError(error: unknown): Error {
  if (error instanceof ApiError) {
    return error;
  }

  return error instanceof Error ? error : new Error("Unknown error");
}
